import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';
import { FiGift, FiUpload, FiInfo, FiPlus } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Callout, calloutVariants } from '@components/Callout';
import { Dropdown } from '@components/Dropdown';
import { addNotification, notificationStates } from '@components/Notification';
import { useEffectiveHolidays } from '@/hooks/useEffectiveHolidays';
import companyService from '@/services/company';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import { HolidayListEditor } from './HolidayListEditor.component';
import { HolidayFormModal } from './HolidayFormModal.component';
import { HolidayImportModal } from './HolidayImportModal.component';
import { holidayTypes } from './HolidayListEditor.constants';

import styles from './HolidaysSection.module.scss';

const COMPANY_OPTION = { value: null, label: 'Company Defaults' };

export const HolidaysSection = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAdmin = useIsAdmin();
  const teamsData = useSelector((state) => state.team?.teams?.data ?? []);

  const contextOptions = useMemo(() => {
    const teamOptions = teamsData
      .filter((team) => !team.teamRemoved)
      .map((team) => ({ value: team.activeTeamid, label: team.Team }));
    return [COMPANY_OPTION, ...teamOptions];
  }, [teamsData]);

  const [selectedContext, setSelectedContext] = useState(COMPANY_OPTION);
  const selectedTeamId = selectedContext?.value ?? null;
  const isTeamContext = selectedTeamId != null;

  const { holidays: effectiveHolidays, setOverrides } =
    useEffectiveHolidays(selectedTeamId);

  const [holidays, setHolidays] = useState([]);
  const [isPersisting, setIsPersisting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Load the effective list (company, or company+team-overrides) into local
  // state whenever it changes (initial fetch, context switch, or a live
  // update from another admin's save).
  useEffect(() => {
    setHolidays(effectiveHolidays);
  }, [effectiveHolidays]);

  const requireAdmin = () => {
    if (isAdmin) return true;
    addNotification({
      message: 'Access denied: Only administrators can manage the holiday list',
      level: notificationStates.error,
    });
    return false;
  };

  // Company-wide write: persists the full list on the company doc. Takes an
  // optional onSaved callback (e.g. close this modal) fired in the same tick
  // as the holidays-list update, not after - otherwise the list update and
  // the modal close land in separate renders, and a still-open modal can
  // re-render in between with its own just-submitted date now present in
  // the list, flagging a false "already exists" against itself.
  const persistHolidays = async (nextHolidays, successMessage, onSaved) => {
    if (!requireAdmin()) return false;

    setIsPersisting(true);
    try {
      const result = await companyService.updateHolidays({
        companyId: userProfile.activeCompany.companyId,
        year: new Date().getFullYear(),
        holidays: nextHolidays,
        updatedBy: userProfile.id,
      });
      if (!result.success) throw new Error(result.error);

      setHolidays(nextHolidays);
      onSaved?.();
      addNotification({
        message: successMessage,
        level: notificationStates.success,
      });
      return true;
    } catch (error) {
      console.error('Error updating holiday list:', error);
      addNotification({
        message: 'Failed to update the holiday list',
        level: notificationStates.error,
      });
      return false;
    } finally {
      setIsPersisting(false);
    }
  };

  // Team-scoped write: touches only this team's override for the given
  // date(s) - the company list and every other team are unaffected. Once a
  // team owns an override for a date, it stays theirs even if the company
  // later edits or removes that date. Takes a { date: holiday|null } map so
  // a single logical edit that spans two dates (e.g. moving a holiday) is
  // one write instead of two. See persistHolidays for why onSaved is fired
  // here rather than by the caller after awaiting.
  const persistTeamOverrides = async (updates, successMessage, onSaved) => {
    if (!requireAdmin()) return false;

    setIsPersisting(true);
    try {
      await setOverrides(updates, userProfile.id);
      onSaved?.();
      addNotification({
        message: successMessage,
        level: notificationStates.success,
      });
      return true;
    } catch (error) {
      console.error('Error updating team holiday override:', error);
      addNotification({
        message: 'Failed to update the holiday list',
        level: notificationStates.error,
      });
      return false;
    } finally {
      setIsPersisting(false);
    }
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setIsModalOpen(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (values) => {
    if (isTeamContext) {
      // Moving a holiday to a new date needs two entries in the same write:
      // free the old date and claim the new one.
      const updates =
        editingHoliday && editingHoliday.date !== values.date
          ? { [editingHoliday.date]: null, [values.date]: values }
          : { [values.date]: values };

      await persistTeamOverrides(
        updates,
        editingHoliday ? 'Holiday updated' : 'Holiday added',
        () => setIsModalOpen(false)
      );
      return;
    }

    const nextHolidays = editingHoliday
      ? holidays.map((holiday) =>
          holiday.date === editingHoliday.date ? values : holiday
        )
      : [...holidays, values];

    await persistHolidays(
      nextHolidays,
      editingHoliday ? 'Holiday updated' : 'Holiday added',
      () => setIsModalOpen(false)
    );
  };

  const handleRemove = (date) =>
    isTeamContext
      ? persistTeamOverrides({ [date]: null }, 'Holiday removed')
      : persistHolidays(
          holidays.filter((holiday) => holiday.date !== date),
          'Holiday removed'
        );

  const handleImportRows = async ({ validRows }) => {
    if (!validRows.length) return false;

    const importedHolidays = validRows.map((row) => ({
      date: row.date,
      name: row.name.trim(),
      optional: row.type === holidayTypes.optional,
      description: (row.description || '').trim(),
    }));

    return persistHolidays(
      [...holidays, ...importedHolidays],
      `${importedHolidays.length} holiday${
        importedHolidays.length > 1 ? 's' : ''
      } imported`,
      () => setIsImportModalOpen(false)
    );
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiGift size={20} color="var(--indigo-600)" />}
        title="Holidays"
        description="Manage the public and festival holidays your company observes each year."
        rightSlot={
          <Dropdown
            options={contextOptions}
            value={selectedContext}
            onChange={setSelectedContext}
            isDisabled={isPersisting}
            width="220px"
            selectStyles={{
              control: { height: '34px' },
              valueContainer: { height: '34px' },
            }}
          />
        }
      />

      <div className={styles.sectionHeading}>
        <div className={styles.sectionHeadingText}>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            {new Date().getFullYear()} Holiday List
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            {isTeamContext
              ? `Holidays for ${selectedContext.label}. Starts from the company list - edit or remove a date to customize it just for this department.`
              : "These dates won't count as absences for any employee."}
          </Text>
        </div>
        <div className={styles.sectionHeadingActions}>
          {!isTeamContext && (
            <Button
              text="Import Holidays"
              variant={buttonVariants.outline}
              icon={<FiUpload size={14} />}
              onClick={() => setIsImportModalOpen(true)}
            />
          )}
          <Button
            text="Add Holiday"
            variant={buttonVariants.primary}
            icon={<FiPlus size={14} />}
            onClick={openAddModal}
          />
        </div>
      </div>

      <HolidayListEditor
        holidays={holidays}
        onEdit={openEditModal}
        onRemove={handleRemove}
        onAdd={openAddModal}
        disabled={isPersisting}
      />

      <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
        Changes will be reflected in employee calendars and attendance reports.
      </Callout>

      <HolidayFormModal
        isVisible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialHoliday={editingHoliday}
        existingDates={holidays.map((holiday) => holiday.date)}
        isSaving={isPersisting}
      />

      <HolidayImportModal
        isVisible={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportRows}
        existingDates={holidays.map((holiday) => holiday.date)}
      />
    </>
  );
};

HolidaysSection.displayName = 'HolidaysSection';
